/**
 * DialogHeader Component
 *
 * Reusable dialog header component with theme integration, help functionality,
 * and support for various configurations. Uses ONLY theme tokens - no hardcoded styles.
 *
 * @example Basic Usage
 * ```tsx
 * import { DialogHeader } from '@modules/ui-components/dialogs/DialogHeader';
 *
 * // Minimal usage
 * <DialogHeader title="My Dialog" />
 *
 * // With subtitle and close button
 * <DialogHeader
 *   title="Settings"
 *   subtitle="Configure your preferences"
 *   onClose={() => console.log('closed')}
 * />
 *
 * // With custom actions and theme variant
 * <DialogHeader
 *   title="Advanced Dialog"
 *   variant="primary"
 *   icon={<SettingsIcon />}
 *   actions={<Button size="small">Save</Button>}
 *   onClose={() => handleClose()}
 * />
 *
 * // With help integration
 * <DialogHeader
 *   title="Help-Enabled Dialog"
 *   helpId="my-help-topic"
 *   onHelpClick={(helpId) => showHelp(helpId)}
 *   helpTooltip="Get help with this dialog"
 * />
 * ```
 *
 * @example Theme Integration
 * ```tsx
 * // All visual variants
 * <DialogHeader title="Default" variant="default" />
 * <DialogHeader title="Primary" variant="primary" />
 * <DialogHeader title="Secondary" variant="secondary" />
 * <DialogHeader title="Elevated" variant="elevated" />
 *
 * // Text alignment options
 * <DialogHeader title="Left" alignment="start" />
 * <DialogHeader title="Center" alignment="center" />
 * <DialogHeader title="Right" alignment="end" />
 * ```
 *
 * @example Advanced Layouts
 * ```tsx
 * // Complex action bar
 * <DialogHeader
 *   title="Document Editor"
 *   icon={<EditIcon />}
 *   actions={
 *     <Box sx={{ display: 'flex', gap: 1 }}>
 *       <Button size="small" variant="outlined">Save</Button>
 *       <Button size="small" variant="contained">Publish</Button>
 *     </Box>
 *   }
 *   onClose={() => handleClose()}
 * />
 * ```
 */
import React, { memo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';

/**
 * DialogHeader component props interface
 */
export interface DialogHeaderProps {
  /**
   * Primary title text (required)
   */
  title: string;

  /**
   * Optional subtitle text displayed below title
   */
  subtitle?: string;

  /**
   * Callback function called when close button is clicked
   */
  onClose?: () => void;

  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Help content identifier for help system integration
   */
  helpId?: string;

  /**
   * Callback function called when help button is clicked
   * Receives the helpId as parameter
   */
  onHelpClick?: (helpId?: string) => void;

  /**
   * Custom tooltip text for help button
   * @default "Get help"
   */
  helpTooltip?: string;

  /**
   * Icon to display before the title
   */
  icon?: React.ReactNode;

  /**
   * Visual variant affecting background color and text color
   * @default "default"
   */
  variant?: 'default' | 'primary' | 'secondary' | 'elevated';

  /**
   * Text alignment within the header
   * @default "start"
   */
  alignment?: 'start' | 'center' | 'end';

  /**
   * Whether to show a bottom divider
   * @default true
   */
  divider?: boolean;

  /**
   * Additional action elements to display in the header
   */
  actions?: React.ReactNode;

  /**
   * Custom styles using theme-aware sx prop
   */
  sx?: SxProps<Theme>;

  /**
   * Accessibility label for the header
   */
  'aria-label'?: string;

  /**
   * Test identifier for automated testing
   */
  'data-testid'?: string;
}

/**
 * DialogHeader Component
 *
 * A flexible and theme-aware dialog header component that supports:
 * - Title and subtitle text
 * - Close button with callback
 * - Help button with callback system
 * - Custom icons and actions
 * - Multiple visual variants
 * - Full theme integration without hardcoded styles
 */
const DialogHeaderComponent: React.FC<DialogHeaderProps> = ({
  title,
  subtitle,
  onClose,
  showCloseButton = true,
  helpId,
  onHelpClick,
  helpTooltip = 'Get help',
  icon,
  variant = 'default',
  alignment = 'center',
  divider = true,
  actions,
  sx,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) => {
  const theme = useTheme();

  // Memoized theme-based style calculations for performance
  const themeStyles = React.useMemo(() => {
    const getBackgroundColor = () => {
      switch (variant) {
        case 'primary':
          return theme.palette.primary.main;
        case 'secondary':
          return theme.palette.secondary.main;
        case 'elevated':
          return theme.palette.background.default;
        default:
          return theme.palette.background.paper;
      }
    };

    const getTextColor = () => {
      switch (variant) {
        case 'primary':
          return theme.palette.primary.contrastText;
        case 'secondary':
          return theme.palette.secondary.contrastText;
        default:
          return theme.palette.text.primary;
      }
    };

    const getSubtitleColor = () => {
      switch (variant) {
        case 'primary':
        case 'secondary':
          return 'inherit';
        default:
          return theme.palette.text.secondary;
      }
    };

    const getHelpButtonColor = () => {
      return variant === 'primary' || variant === 'secondary'
        ? 'inherit'
        : theme.palette.text.secondary;
    };

    const getHelpButtonHoverColor = () => {
      return variant === 'primary' || variant === 'secondary'
        ? 'inherit'
        : theme.palette.primary.main;
    };

    return {
      backgroundColor: getBackgroundColor(),
      textColor: getTextColor(),
      subtitleColor: getSubtitleColor(),
      helpButtonColor: getHelpButtonColor(),
      helpButtonHoverColor: getHelpButtonHoverColor(),
    };
  }, [theme, variant]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleClose = React.useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleHelpClick = React.useCallback(() => {
    onHelpClick?.(helpId);
  }, [onHelpClick, helpId]);

  // iOS navigation bar layout: close LEFT, title CENTER, actions/help RIGHT.
  // Uses CSS Grid (not flex) so title stays symmetrically centred even when
  // the right cell is empty. A simple flex `justify-content: space-between`
  // would prize title left when nothing sits on the right — we don't want
  // that. Left and right tracks have matching min widths so the phantom
  // balance holds in both directions.
  //
  // Legacy `alignment` prop: `alignment === 'start'` opts out of iOS layout
  // and falls back to the old left-aligned block (title at the leading
  // edge). This preserves backward compatibility for dialog headers that
  // intentionally use a left-aligned title (e.g. split-pane app headers).
  const useCenteredLayout = alignment === 'center';
  const slotMinWidth = theme.spacing(5); // 40px — matches IconButton width
  return (
    <Box
      sx={{
        display: useCenteredLayout ? 'grid' : 'flex',
        gridTemplateColumns: useCenteredLayout
          ? `${slotMinWidth} 1fr ${slotMinWidth}`
          : undefined,
        alignItems: 'center',
        justifyContent: useCenteredLayout
          ? undefined
          : alignment === 'end'
          ? 'flex-end'
          : 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(2),
        backgroundColor: themeStyles.backgroundColor,
        color: themeStyles.textColor,
        borderBottom: divider ? `1px solid ${theme.palette.divider}` : 'none',
        minHeight: theme.spacing(7), // Consistent minimum height
        ...sx,
      }}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {/* LEFT SLOT: close button (or empty phantom for balance). */}
      {useCenteredLayout && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: slotMinWidth,
          }}
        >
          {showCloseButton && (
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: 'inherit',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&:focus': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }}
              aria-label="close dialog"
              tabIndex={0}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* CENTRE SLOT: icon + title/subtitle. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
          justifyContent: useCenteredLayout ? 'center' : 'flex-start',
          minWidth: 0, // allow truncation inside grid track
          flex: useCenteredLayout ? undefined : 1,
        }}
      >
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'inherit',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}

        <Box sx={{ minWidth: 0, textAlign: useCenteredLayout ? 'center' : 'left' }}>
          <Typography
            variant="h6"
            component="h2"
            role="heading"
            sx={{
              color: 'inherit',
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1.2,
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: themeStyles.subtitleColor,
                opacity: variant === 'primary' || variant === 'secondary' ? 0.8 : 1,
                display: 'block',
                lineHeight: 1.2,
                marginTop: theme.spacing(0.25),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* RIGHT SLOT: actions + help button (close is now on the left).
          Rendered even when empty to preserve grid symmetry via
          minWidth — otherwise the centre title drifts left when no
          actions/help exist. */}
      {useCenteredLayout ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: theme.spacing(1),
            minWidth: slotMinWidth,
          }}
          data-testid="header-actions"
        >
          {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              {actions}
            </Box>
          )}

          {helpId && (
            <Tooltip title={helpTooltip} arrow>
              <IconButton
                size="small"
                onClick={handleHelpClick}
                sx={{
                  color: themeStyles.helpButtonColor,
                  '&:hover': {
                    color: themeStyles.helpButtonHoverColor,
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&:focus': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
                aria-label="help"
                data-testid="dialog-header-help-button"
                tabIndex={0}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        // Legacy `alignment === 'start'` path — keep original right-side
        // cluster with close button on the right for full backward compat.
        (actions || helpId || showCloseButton) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
            data-testid="header-actions"
          >
            {actions && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                {actions}
              </Box>
            )}

            {helpId && (
              <Tooltip title={helpTooltip} arrow>
                <IconButton
                  size="small"
                  onClick={handleHelpClick}
                  sx={{
                    color: themeStyles.helpButtonColor,
                    '&:hover': {
                      color: themeStyles.helpButtonHoverColor,
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&:focus': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }}
                  aria-label="help"
                  data-testid="dialog-header-help-button"
                  tabIndex={0}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {showCloseButton && (
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: 'inherit',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&:focus': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
                aria-label="close dialog"
                tabIndex={0}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )
      )}
    </Box>
  );
};

/**
 * Memoized DialogHeader component for performance optimization
 * Prevents unnecessary re-renders when props haven't changed
 *
 * Custom comparison function for shallow prop comparison
 */
export const DialogHeader = memo(DialogHeaderComponent, (prevProps, nextProps) => {
  // Shallow comparison of all primitive props
  const primitiveKeys: (keyof DialogHeaderProps)[] = [
    'title', 'subtitle', 'showCloseButton', 'helpId', 'helpTooltip',
    'variant', 'alignment', 'divider', 'aria-label', 'data-testid'
  ];

  // Check primitive props
  for (const key of primitiveKeys) {
    if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }

  // Check function props (these should be stable references)
  if (prevProps.onClose !== nextProps.onClose) {
    return false;
  }

  if (prevProps.onHelpClick !== nextProps.onHelpClick) {
    return false;
  }

  // For complex props like icon, actions, and sx, we do reference comparison
  // Parents should memoize these props if they want to avoid re-renders
  if (prevProps.icon !== nextProps.icon) {
    return false;
  }

  if (prevProps.actions !== nextProps.actions) {
    return false;
  }

  if (prevProps.sx !== nextProps.sx) {
    return false;
  }

  return true;
});

// Default export
export default DialogHeader;